"use client";

import React, { useState } from "react";
import { CircleCheck, FileText, SlidersHorizontal as Sliders, ShieldCheck, ArrowRight, Copy, Check, Info, Ruler, Boxes, FileSpreadsheet, Cpu } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { useLanguage } from "@/hooks/useLanguage";
import { pick, type Lang } from "@/lib/i18n";
import { COMMON } from "@/lib/commonCopy";
const samplePoSnippet: Record<Lang, string> = {
  en: `PO SPECIFICATION BLOCK: WEDGE WIRE SCREEN
------------------------------------------------------------
Item Description       : Custom Welded Cylindrical Wedge Wire Filter Element
Flow Direction         : Flow-Out-To-In (FOTI) / Surface Wire Outside
Nominal Slot Opening   : 100 Microns (0.100 mm) ± 0.015 mm
Surface Wire Profile   : Profile 18S (Width: 1.5 mm, Height: 2.5 mm, 12° Relief Angle)
Support Rod Profile    : Q25 (Width: 2.0 mm, Height: 3.5 mm) @ 20 mm Pitch
Alloy Grade            : 316L Stainless Steel (UNS S31603) per ASTM A580
Dimensions             : OD 152.4 mm (6.00") x Length 914.4 mm (36.00")
End Fittings           : Top ANSI Class 150 Flange (316L), Bottom Blind Cap Welded
Surface Finish         : Full Acid Pickling & Passivation per ASTM A380
Quality Documentation  : Optical Slot Measurement Protocol, Material Test Report (MTR) upon request
------------------------------------------------------------`,
  zh: `采购订单技术规范块：楔形丝滤芯 (WEDGE WIRE SCREEN)
------------------------------------------------------------
物料名称与类型         : 定制熔焊圆柱形楔形丝滤芯 (Cylindrical Filter Element)
过滤流向               : 外流至内 (FOTI) / 表面楔形丝位于外侧
公称缝隙开孔           : 100 微米 (0.100 mm) ± 0.015 mm
表面丝型截面           : Profile 18S (丝宽: 1.5 mm, 丝高: 2.5 mm, 12° 释放角)
支撑筋条截面与间距     : Q25 (宽: 2.0 mm, 高: 3.5 mm) @ 20 mm 间距
材料合金牌号           : 316L 不锈钢 (UNS S31603) 符合 ASTM A580
外形几何尺寸           : 外径 OD 152.4 mm (6.00") x 长度 914.4 mm (36.00")
端部连接形式           : 上端 ANSI Class 150 法兰 (316L), 下端盲板满焊封堵
表面处理工艺           : 全酸洗钝化处理 符合 ASTM A380 标准
质量检验与认证文件     : 光学缝隙实测报告, 可按需提供材质证明书 (MTR)
------------------------------------------------------------`,
  es: `BLOQUE DE ESPECIFICACIÓN DE PEDIDO (PO): MALLA DE ALAMBRE EN CUÑA
------------------------------------------------------------
Descripción del artículo: Elemento filtrante cilíndrico soldado en alambre de cuña
Dirección de flujo     : Flujo de fuera hacia adentro (FOTI) / Alambre exterior
Abertura de ranura nom. : 100 micrones (0,100 mm) ± 0,015 mm
Perfil de alambre sup. : Perfil 18S (Ancho: 1,5 mm, Alto: 2,5 mm, Ángulo de alivio 12°)
Perfil varilla soporte : Q25 (Ancho: 2,0 mm, Alto: 3,5 mm) @ paso de 20 mm
Grado de aleación      : Acero inoxidable 316L (UNS S31603) según ASTM A580
Dimensiones            : DE 152,4 mm (6,00") x Longitud 914,4 mm (36,00")
Conexiones extremas    : Brida superior ANSI Clase 150 (316L), tapa ciega inferior soldada
Acabado superficial    : Decapado y pasivado ácido integral según ASTM A380
Documentación calidad  : Protocolo óptico de medición de ranura, certificado MTR a solicitud
------------------------------------------------------------`,
  de: `BESTELLSPEZIFIKATIONSBLOCK: SPALTSIEB-FILTERELEMENT
------------------------------------------------------------
Artikelbeschreibung    : Geschweißtes zylindrisches Spaltsieb-Filterelement
Durchflussrichtung     : Von außen nach innen (FOTI) / Profildraht außen
Nennspaltweite         : 100 Mikrometer (0,100 mm) ± 0,015 mm
Oberflächendrahtprofil : Profil 18S (Breite: 1,5 mm, Höhe: 2,5 mm, Freiwinkel 12°)
Tragstabprofil         : Q25 (Breite: 2,0 mm, Höhe: 3,5 mm) @ 20 mm Teilung
Legierungsqualität     : Edelstahl 316L (UNS S31603) nach ASTM A580
Abmessungen            : AD 152,4 mm (6,00") x Länge 914,4 mm (36,00")
Endanschlüsse          : Oberer Flansch ANSI Class 150 (316L), unterer Blinddeckel verschweißt
Oberflächenbehandlung  : Vollständige Säurebeizung & Passivierung nach ASTM A380
Qualitätsdokumentation : Optisches Spaltmessprotokoll, Werkszeugnis (MTR) auf Anfrage
------------------------------------------------------------`,
  fr: `BLOC DE SPÉCIFICATION DE COMMANDE (PO) : GRILLE EN FIL PROFILÉ
------------------------------------------------------------
Description article    : Élément filtrant cylindrique soudé en fil profilé en V
Sens d'écoulement      : De l'extérieur vers l'intérieur (FOTI) / Fil profilé à l'extérieur
Ouverture de fente nom. : 100 microns (0,100 mm) ± 0,015 mm
Profil de fil surface  : Profil 18S (Largeur : 1,5 mm, Hauteur : 2,5 mm, Dépouille 12°)
Profil tige support    : Q25 (Largeur : 2,0 mm, Hauteur : 3,5 mm) @ pas de 20 mm
Nuance d'alliage       : Acier inoxydable 316L (UNS S31603) selon ASTM A580
Dimensions             : DE 152,4 mm (6,00") x Longueur 914,4 mm (36,00")
Raccords d'extrémité   : Bride supérieure ANSI Classe 150 (316L), fond plein inférieur soudé
Finition de surface    : Décapage et passivation acide complète selon ASTM A380
Documentation qualité  : Protocole optique de mesure de fente, certificat MTR sur demande
------------------------------------------------------------`,
  pt: `BLOCO DE ESPECIFICAÇÃO DE PEDIDO (PO): TELA DE ARAME EM CUNHA
------------------------------------------------------------
Descrição do item      : Elemento filtrante cilíndrico soldado de arame perfilado
Direção de fluxo       : Fluxo de fora para dentro (FOTI) / Arame na superfície externa
Abertura nominal fenda : 100 mícrones (0,100 mm) ± 0,015 mm
Perfil do arame        : Perfil 18S (Largura: 1,5 mm, Altura: 2,5 mm, Ângulo alívio 12°)
Perfil haste suporte   : Q25 (Largura: 2,0 mm, Altura: 3,5 mm) @ passo de 20 mm
Grau da liga metálica  : Aço inoxidável 316L (UNS S31603) conforme ASTM A580
Dimensões              : DE 152,4 mm (6,00") x Comprimento 914,4 mm (36,00")
Conexões terminais     : Flange superior ANSI Classe 150 (316L), tampa cega inferior soldada
Acabamento superficial : Decapagem e passivação ácida completa conforme ASTM A380
Documentação qualidade : Protocolo óptico de medição de fenda, certificado MTR sob solicitação
------------------------------------------------------------`
};
function TechnicalDocsConsumer({
  lang
}: {
  lang: Lang;
}) {
  const {
    posts,
    loading,
    error
  } = usePosts();
  const labels = {
    empty: pick(lang, {
      en: "Additional engineering guides and standard procurement documentation are ready in our central repository.",
      zh: "更多工程指南与标准采购文档已归档在我们的技术资源中心。",
      es: "Hay guías de ingeniería adicionales y documentación estándar de compras en nuestro repositorio central.",
      de: "Weitere technische Leitfäden und Beschaffungsunterlagen sind in unserer Dokumentenbibliothek verfügbar.",
      fr: "Des guides d'ingénierie supplémentaires et la documentation d'achat standard sont disponibles dans notre centre technique.",
      pt: "Guias de engenharia adicionais e documentação técnica de compras estão disponíveis em nosso repositório central."
    }),
    browse: pick(lang, {
      en: "Browse Technical Resources",
      zh: "浏览技术资源",
      es: "Explorar recursos técnicos",
      de: "Technische Ressourcen durchsuchen",
      fr: "Parcourir les ressources techniques",
      pt: "Navegar pelos recursos técnicos"
    }),
    fallbackCategory: pick(lang, {
      en: "Engineering Data",
      zh: "工程数据",
      es: "Datos de ingeniería",
      de: "Technische Daten",
      fr: "Données d'ingénierie",
      pt: "Dados de engenharia"
    }),
    fallbackFormat: pick(lang, {
      en: "PDF Specs",
      zh: "PDF 规格书",
      es: "Especificaciones PDF",
      de: "PDF-Datenblatt",
      fr: "Spécifications PDF",
      pt: "Especificações PDF"
    }),
    pgs: pick(lang, {
      en: "pgs",
      zh: "页",
      es: "págs.",
      de: "S.",
      fr: "pages",
      pt: "págs."
    }),
    viewSpec: pick(lang, {
      en: "View Spec",
      zh: "查看规范",
      es: "Ver especificación",
      de: "Spezifikation ansehen",
      fr: "Voir la spécification",
      pt: "Ver especificação"
    })
  };
  if (loading) {
    return <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        {[0, 1, 2].map((_, i) => <div key={i} data-index={i} className="h-44 rounded-lg border border-border bg-muted/40 p-5" />)}
      </div>;
  }
  if (error || posts.length === 0) {
    return <div className="rounded-lg border border-border bg-card p-6 text-center text-card-foreground">
        <p className="mf-caption text-muted-foreground">{labels.empty}</p>
        <div className="mt-4">
          <Button asChild variant="outline" size="sm">
            <Link to="/TechnicalResourcesPage" className="inline-flex items-center gap-1.5">
              <span>{labels.browse}</span>
              <ArrowRight className="size-4" aria-hidden="true" />
            </Link>
          </Button>
        </div>
      </div>;
  }
  return <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
      {posts.map((post, i) => {
      const category = post.customFields?.category || labels.fallbackCategory;
      const docCode = post.customFields?.doc_code || "PO-REF";
      const formatLabel = post.customFields?.format_label || labels.fallbackFormat;
      const pageCount = post.customFields?.page_count;
      return <Card key={post.id} data-index={i} className="flex flex-col justify-between border-border bg-card rounded-lg transition-colors hover:border-primary/40">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between gap-2">
                <Badge variant="outline" className="font-mono mf-caption bg-card">
                  {docCode}
                </Badge>
                <span className="mf-caption text-muted-foreground">{category}</span>
              </div>
              <CardTitle className="mt-2 text-base font-semibold text-card-foreground">
                {post.title}
              </CardTitle>
              {post.customFields?.summary && <CardDescription className="line-clamp-2 mf-caption">
                  {post.customFields.summary}
                </CardDescription>}
            </CardHeader>
            <CardContent className="pt-0">
              <div className="flex items-center justify-between border-t border-border pt-3">
                <div className="flex items-center gap-1.5 mf-caption text-muted-foreground">
                  <FileText className="size-3.5" aria-hidden="true" />
                  <span>{formatLabel}</span>
                  {pageCount && <span>({pageCount} {labels.pgs})</span>}
                </div>
                <Button asChild variant="ghost" size="sm" className="px-2 mf-caption text-primary">
                  <Link to={post.link || "/TechnicalResourcesPage"} className="inline-flex items-center gap-1">
                    <span>{labels.viewSpec}</span>
                    <ArrowRight className="size-3.5" aria-hidden="true" />
                  </Link>
                </Button>
              </div>
            </CardContent>
          </Card>;
    })}
    </div>;
}
export default function PurchaseSpecifications() {
  const [copied, setCopied] = useState(false);
  const {
    lang
  } = useLanguage();
  const copy = {
    eyebrow: pick(lang, {
      en: "Industrial Procurement & Engineering Standards",
      zh: "工业采购与工程标准",
      es: "Estándares de compras industriales e ingeniería",
      de: "Industriebeschaffung & technische Normen",
      fr: "Approvisionnement industriel et normes d'ingénierie",
      pt: "Padrões de compras industriais e engenharia"
    }),
    heading: pick(lang, {
      en: "Specify wedge wire screens with precision",
      zh: "精准规范楔形丝滤网技术参数",
      es: "Especifique mallas de alambre en cuña con precisión",
      de: "Spaltsiebe mit technischer Präzision spezifizieren",
      fr: "Spécifiez les grilles à fil profilé avec précision",
      pt: "Especifique telas de arame em cunha com precisão"
    }),
    lead: pick(lang, {
      en: "Include verified mechanical tolerances, profile codes, and alloy standards on your purchase orders to ensure exact fabrication and smooth qualification.",
      zh: "在采购订单 (PO) 中明确机械公差、型材代号与合金标准，以确保精确制造与可靠验收。",
      es: "Incluya tolerancias mecánicas verificadas, códigos de perfil y normas de aleación en sus órdenes de compra para asegurar una fabricación exacta.",
      de: "Definieren Sie mechanische Toleranzen, Profilcodes und Legierungsstandards in Ihren Bestellungen für präzise Fertigung und fehlerfreie Abnahme.",
      fr: "Intégrez les tolérances mécaniques, codes de profils et normes d'alliages à vos bons de commande pour garantir une fabrication rigoureuse.",
      pt: "Inclua tolerâncias mecânicas verificadas, códigos de perfil e normas de ligas em seus pedidos de compra para assegurar fabricação exata."
    }),
    tableTitle: pick(lang, {
      en: "Critical Purchase Order Checklist",
      zh: "关键采购订单 (PO) 规范核对清单",
      es: "Lista de verificación crítica para órdenes de compra",
      de: "Checkliste für kritische Bestellangaben (PO)",
      fr: "Liste de contrôle essentielle pour commande (PO)",
      pt: "Lista de verificação crítica para pedidos de compra"
    }),
    tableSubtitle: pick(lang, {
      en: "Mandatory parameters required for error-free quotation and accurate production tooling.",
      zh: "零差错报价与模具排产所需的必填参数。",
      es: "Parámetros obligatorios requeridos para cotizaciones sin errores y herramientas de producción precisas.",
      de: "Erforderliche Pflichtparameter für fehlerfreie Angebote und maßgenaue Werkzeugabstimmung.",
      fr: "Paramètres obligatoires nécessaires pour un chiffrage sans erreur et un outillage de production exact.",
      pt: "Parâmetros obrigatórios para cotações sem erros e ferramental de produção exato."
    }),
    rfqBtn: pick(lang, {
      en: "Submit Custom RFQ",
      zh: "提交定制询价",
      es: "Enviar solicitud de cotización",
      de: "Kundenspezifische Anfrage senden",
      fr: "Soumettre une demande de devis",
      pt: "Enviar solicitação de cotação"
    }),
    thParameter: pick(lang, {
      en: "Parameter",
      zh: "规范参数",
      es: "Parámetro",
      de: "Parameter",
      fr: "Paramètre",
      pt: "Parâmetro"
    }),
    thRange: pick(lang, {
      en: "Available Industry Range",
      zh: "行业通用规格范围",
      es: "Rango industrial disponible",
      de: "Verfügbarer Industriebereich",
      fr: "Plage industrielle disponible",
      pt: "Faixa industrial disponível"
    }),
    thTolerance: pick(lang, {
      en: "Manufacturing Tolerance",
      zh: "制造公差要求",
      es: "Tolerancia de fabricación",
      de: "Fertigungstoleranz",
      fr: "Tolérance de fabrication",
      pt: "Tolerância de fabricação"
    }),
    thClause: pick(lang, {
      en: "PO Engineering Clause",
      zh: "PO 工程技术条款建议",
      es: "Cláusula de ingeniería para PO",
      de: "Empfohlene Formulierung im Bestelltext",
      fr: "Clause technique recommandée pour PO",
      pt: "Cláusula técnica recomendada para PO"
    }),
    // Card 1
    card1Title: pick(lang, {
      en: "Slot Size & Relief Geometry",
      zh: "缝隙尺寸与释放角几何结构",
      es: "Abertura de ranura y geometría de alivio",
      de: "Spaltweite & Freiwinkel-Geometrie",
      fr: "Taille de fente et géométrie de dépouille",
      pt: "Tamanho de fenda e geometria de alívio"
    }),
    card1Desc: pick(lang, {
      en: "V-shaped wire profile creates two-point contact to eliminate blinding and backwash drag.",
      zh: "V型丝几何截面形成两点接触开孔，杜绝颗粒卡堵并大幅降低反冲洗阻力。",
      es: "El perfil de alambre en V genera contacto en dos puntos para evitar colmataciones y reducir la fricción en retrolavado.",
      de: "V-Drahtprofile erzeugen eine Zweipunktauflage, verhindern Verstopfungen und minimieren den Rückspülwiderstand.",
      fr: "La géométrie en V assure un contact en deux points, éliminant les blocages de particules et les pertes de charge en rétrolavage.",
      pt: "O perfil de arame em V cria contato em dois pontos para eliminar entupimentos e reduzir o arrasto de retrolavagem."
    }),
    card1L1Label: pick(lang, {
      en: "Slot Range",
      zh: "缝隙范围",
      es: "Rango de ranura",
      de: "Spaltweitenbereich",
      fr: "Plage de fente",
      pt: "Faixa de fenda"
    }),
    card1L2Label: pick(lang, {
      en: "Relief Angles",
      zh: "释放角规格",
      es: "Ángulos de alivio",
      de: "Freiwinkel",
      fr: "Angles de dépouille",
      pt: "Ângulos de alívio"
    }),
    card1L3Label: pick(lang, {
      en: "Inspection Method",
      zh: "检验方式",
      es: "Método de inspección",
      de: "Prüfmethode",
      fr: "Méthode d'inspection",
      pt: "Método de inspeção"
    }),
    card1L3Val: pick(lang, {
      en: "Optical CMM / Feeler Gauge",
      zh: "光学影像测量仪 (CMM) / 塞规",
      es: "CMM óptica / Galgas de espesores",
      de: "Optische KMG / Fühlerlehre",
      fr: "MMT optique / Jauges d'épaisseur",
      pt: "CMM óptica / Calibrador de folga"
    }),
    card1Text: pick(lang, {
      en: "Specify whether continuous slots run along the axis (for axial dewatering) or around the circumference (for rotary drums).",
      zh: "需在 PO 中指明连续缝隙是沿轴向排列（用于轴向物料流动与脱水）还是沿圆周环向排列（用于旋转转鼓筛）。",
      es: "Especifique si las ranuras continuas van axiales (para deshidratación axial) o circunferenciales (para tambores rotativos).",
      de: "Geben Sie an, ob die kontinuierlichen Spalte axial (axiale Entwässerung) oder radial umlaufend (Drehtrommeln) verlaufen.",
      fr: "Précisez si les fentes continues sont disposées axialement (égouttage axial) ou circonférentiellement (tambours rotatifs).",
      pt: "Especifique se as fendas contínuas correm axialmente (desaguamento axial) ou circunferencialmente (tambores rotativos)."
    }),
    // Card 2
    card2Title: pick(lang, {
      en: "Alloy Grade & Metallurgical Compliance",
      zh: "合金牌号与冶金规范合规",
      es: "Grado de aleación y cumplimiento metalúrgico",
      de: "Legierungsgrad & metallurgische Konformität",
      fr: "Nuance d'alliage et conformité métallurgique",
      pt: "Grau da liga e conformidade metalúrgica"
    }),
    card2Desc: pick(lang, {
      en: "Corrosion resistance matched to specific process chemistry and thermal cycles.",
      zh: "根据工艺介质化学腐蚀性及操作温度循环精准匹配合金牌号。",
      es: "Resistencia a la corrosión adaptada a la química de proceso y a los ciclos térmicos específicos.",
      de: "Korrosionsbeständigkeit abgestimmt auf Prozesschemie und thermische Betriebszyklen.",
      fr: "Résistance à la corrosion adaptée à la chimie de procédé et aux cycles thermiques spécifiques.",
      pt: "Resistência à corrosão adequada à química do processo e ciclos térmicos específicos."
    }),
    card2L1Label: pick(lang, {
      en: "Standard Alloys",
      zh: "常用合金",
      es: "Aleaciones estándar",
      de: "Standardlegierungen",
      fr: "Alliages standard",
      pt: "Ligas padrão"
    }),
    card2L2Label: pick(lang, {
      en: "Duplex & Superalloys",
      zh: "双相钢与特种合金",
      es: "Dúplex y superaleaciones",
      de: "Duplex & Superlegierungen",
      fr: "Duplex et superalliages",
      pt: "Duplex e superligas"
    }),
    card2L3Label: pick(lang, {
      en: "Material Testing",
      zh: "材质检测报告",
      es: "Ensayos de materiales",
      de: "Werkstoffprüfung",
      fr: "Essais matériaux",
      pt: "Ensaios de materiais"
    }),
    card2L3Val: pick(lang, {
      en: "MTR / PMI Upon Request",
      zh: "可按需提供 MTR / PMI",
      es: "MTR / PMI bajo solicitud",
      de: "MTR / PMI auf Anfrage",
      fr: "MTR / PMI sur demande",
      pt: "MTR / PMI sob solicitação"
    }),
    card2Text: pick(lang, {
      en: "For high-chloride applications, Positive Material Identification (PMI) testing is available upon request.",
      zh: "在高氯化物或酸性工况下，可按订单要求提供 PMI 光谱检测与材料材质报告。",
      es: "Para aplicaciones con alto contenido de cloruros, ensayos de identificación positiva de materiales (PMI) disponibles a pedido.",
      de: "Für chloridhaltige Medien ist eine PMI-Werkstoffanalyse (Positive Material Identification) auf Anfrage verfügbar.",
      fr: "Pour les milieux riches en chlorures, des contrôles PMI (identification des métaux) sont disponibles sur demande.",
      pt: "Para fluidos com alto teor de cloretos, testes de identificação positiva de materiais (PMI) estão disponíveis sob pedido."
    }),
    // Card 3
    card3Title: pick(lang, {
      en: "Support Rod Pitch & Structural Rigidity",
      zh: "支撑杆间距与结构刚度",
      es: "Paso de varillas de soporte y rigidez estructural",
      de: "Tragstabteilung & Struktursteifigkeit",
      fr: "Espacement des tiges de support et rigidité",
      pt: "Passo das hastes de suporte e rigidez estrutural"
    }),
    card3Desc: pick(lang, {
      en: "Structural resistance to differential pressure, vibration, and cake scraper loads.",
      zh: "抵抗高压差冲击、机械振动载荷以及滤饼刮刀接触剪切力。",
      es: "Resistencia estructural ante presiones diferenciales, vibraciones mecánicas y cargas de raspadores de torta.",
      de: "Strukturelle Beständigkeit gegen Differenzdruck, Vibrationslasten und Schaberklingen.",
      fr: "Résistance structurelle à la pression différentielle, aux vibrations et aux efforts de racleurs de gâteau.",
      pt: "Resistência estrutural a pressões diferenciais, vibrações mecânicas e cargas de raspadores de torta."
    }),
    card3L1Label: pick(lang, {
      en: "Pitch Options",
      zh: "间距规格",
      es: "Opciones de paso",
      de: "Teilungsoptionen",
      fr: "Options d'espacement",
      pt: "Opções de passo"
    }),
    card3L1Val: pick(lang, {
      en: "10 mm to 50 mm C-to-C",
      zh: "10 mm 至 50 mm 中心距",
      es: "10 mm a 50 mm entre centros",
      de: "10 mm bis 50 mm Achsabstand",
      fr: "10 mm à 50 mm d'axe en axe",
      pt: "10 mm a 50 mm centro a centro"
    }),
    card3L2Label: pick(lang, {
      en: "Rod Geometries",
      zh: "支撑杆截面",
      es: "Geometría de varillas",
      de: "Tragstabgeometrien",
      fr: "Géométries de barres",
      pt: "Geometrias de hastes"
    }),
    card3L2Val: pick(lang, {
      en: "Round, Q-Bar, Flat Bar",
      zh: "圆棒、Q型梯形杆、扁钢",
      es: "Redonda, barra Q, barra plana",
      de: "Rundstab, Q-Profil, Flachstahl",
      fr: "Rond, barre Q, méplat",
      pt: "Redonda, barra Q, barra chata"
    }),
    card3L3Label: pick(lang, {
      en: "Fusion Technique",
      zh: "焊接熔接工艺",
      es: "Técnica de unión",
      de: "Schweißverfahren",
      fr: "Procédé de soudage",
      pt: "Técnica de união"
    }),
    card3L3Val: pick(lang, {
      en: "Resistance Weld At Each Cross",
      zh: "每个交叉节点全自动电阻熔焊",
      es: "Soldadura por resistencia en cada cruce",
      de: "Widerstandsschweißung an jedem Kreuzungspunkt",
      fr: "Soudure par résistance à chaque intersection",
      pt: "Solda por resistência em cada cruzamento"
    }),
    card3Text: pick(lang, {
      en: "State required collapse and burst pressure ratings to allow our engineering team to calculate optimum wire-to-rod ratios.",
      zh: "在采购需求中注明设计压溃与爆破压力等级，以便工程团队计算最优丝型与支撑杆配比。",
      es: "Indique las presiones de colapso y rotura para que nuestro equipo calcule la relación óptima entre alambre y soporte.",
      de: "Geben Sie Berstdrücke und Kollapsgrenzen an, damit unser Team die optimalen Draht-zu-Tragstab-Verhältnisse ermitteln kann.",
      fr: "Indiquez les pressions d'écrasement et d'éclatement pour permettre le dimensionnement optimal du ratio fil/tige.",
      pt: "Indique a pressão de colapso e ruptura exigidas para cálculo da relação ideal entre arame e suporte."
    }),
    // Template Box
    templateTitle: pick(lang, {
      en: "Ready-to-Use Purchase Order Spec Template",
      zh: "可直接复制的采购订单 (PO) 规范模板",
      es: "Plantilla de especificación de orden de compra lista para usar",
      de: "Sofort nutzbare Bestelltext-Vorlage (PO)",
      fr: "Modèle de spécification pour bon de commande (PO)",
      pt: "Modelo de especificação de pedido de compra pronto para uso"
    }),
    copyBtn: pick(lang, {
      en: "Copy Text",
      zh: "复制文本",
      es: "Copiar texto",
      de: "Text kopieren",
      fr: "Copier le texte",
      pt: "Copiar texto"
    }),
    copiedBtn: pick(lang, {
      en: "Copied",
      zh: "已复制",
      es: "Copiado",
      de: "Kopiert",
      fr: "Copié",
      pt: "Copiado"
    }),
    templateNote: pick(lang, {
      en: "Paste this block directly into your ERP or purchase order document and update the variables for your specific filter vessel.",
      zh: "将此文本块直接粘贴至您的 ERP 或采购订单文件中，并根据具体过滤容器修改参数变量。",
      es: "Pegue este bloque directamente en su sistema ERP o documento de compra y ajuste las variables de su recipiente de filtración.",
      de: "Fügen Sie diesen Text direkt in Ihr ERP-System oder Ihre Bestellung ein und passen Sie die Parameter an Ihren Filterbehälter an.",
      fr: "Collez ce texte directement dans votre ERP ou vos bons de commande et ajustez les variables à votre cuve de filtration.",
      pt: "Cole este bloco diretamente em seu sistema ERP ou pedido de compra e adapte as variáveis para o seu vaso de filtração."
    }),
    // Right review box
    reviewEyebrow: pick(lang, {
      en: "Technical Review Support",
      zh: "技术评审支持",
      es: "Soporte de revisión técnica",
      de: "Technische Prüfung & Beratung",
      fr: "Support de révision technique",
      pt: "Suporte de revisão técnica"
    }),
    reviewTitle: pick(lang, {
      en: "Need engineering validation before order release?",
      zh: "下单前需要工程图纸与参数验证？",
      es: "¿Requiere validación de ingeniería antes de emitir la orden?",
      de: "Benötigen Sie technische Validierung vor Freigabe der Bestellung?",
      fr: "Besoin d'une validation technique avant émission de la commande ?",
      pt: "Precisa de validação de engenharia antes de emitir o pedido?"
    }),
    reviewDesc: pick(lang, {
      en: "Our application engineers review your CAD models, operating temperatures, differential pressures, and slurry abrasive contents to confirm wire cross-sections before manufacturing.",
      zh: "我们的应用工程师可在排产前复核您的 CAD 图纸、操作温度、压差载荷及介质特性，确认最佳丝材截面。",
      es: "Nuestros ingenieros revisan sus modelos CAD, temperaturas de operación, presiones diferenciales y fluidos para confirmar las secciones antes de fabricar.",
      de: "Unsere Anwendungsingenieure prüfen CAD-Dateien, Betriebstemperaturen, Differenzdrücke und Partikelabrieb vor Fertigungsfreigabe.",
      fr: "Nos ingénieurs examinent vos modèles CAD, températures de service, pressions différentielles et caractéristiques des fluides avant fabrication.",
      pt: "Nossos engenheiros revisam modelos CAD, temperaturas operacionais, pressões diferenciais e características de abrasão antes da produção."
    }),
    reviewPt1: pick(lang, {
      en: "CAD drawing verification (.DXF / .STEP format)",
      zh: "CAD 工程图纸核对与解析（.DXF / .STEP 格式）",
      es: "Verificación de planos CAD (formatos .DXF / .STEP)",
      de: "CAD-Zeichnungsprüfung (.DXF / .STEP-Formate)",
      fr: "Vérification des plans CAD (formats .DXF / .STEP)",
      pt: "Verificação de desenhos CAD (formatos .DXF / .STEP)"
    }),
    reviewPt2: pick(lang, {
      en: "Open area (%) vs. hydraulic flow velocity calculations",
      zh: "开孔率 (%) 与水力流通流速水头损失计算",
      es: "Cálculo de área abierta (%) frente a velocidad de flujo hidráulico",
      de: "Berechnung von freier Siebfläche (%) und hydraulischer Strömungsgeschwindigkeit",
      fr: "Calcul de surface ouverte (%) et vitesse découlement hydraulique",
      pt: "Cálculo de área aberta (%) versus velocidade de fluxo hidráulico"
    }),
    reviewPt3: pick(lang, {
      en: "Custom flange, collar, and reinforcement ring design",
      zh: "定制法兰、加固护圈与接口过渡段设计",
      es: "Diseño de bridas personalizadas, collares y anillos de refuerzo",
      de: "Sonderflansche, Einfassungen und Verstärkungsringe nach Maß",
      fr: "Conception de brides sur mesure, viroles et bagues de renfort",
      pt: "Projeto de flanges sob medida, colares e anéis de reforço"
    }),
    speakEngineer: pick(lang, {
      en: "Speak with an Engineer",
      zh: "咨询应用工程师",
      es: "Hablar con un ingeniero",
      de: "Mit Ingenieur sprechen",
      fr: "Échanger avec un ingénieur",
      pt: "Falar com um engenheiro"
    }),
    selectionGuideBtn: pick(lang, {
      en: "Selection Guide",
      zh: "选型指南",
      es: "Guía de selección",
      de: "Auswahlleitfaden",
      fr: "Guide de sélection",
      pt: "Guia de seleção"
    }),
    // Bottom docs section
    docsTitle: pick(lang, {
      en: "Related Technical Documents & Datasheets",
      zh: "相关技术文档与工程规格书",
      es: "Documentos técnicos y fichas técnicas relacionadas",
      de: "Zugehörige technische Dokumente & Datenblätter",
      fr: "Documents techniques et fiches de spécification associées",
      pt: "Documentos técnicos e fichas de especificação relacionadas"
    }),
    docsDesc: pick(lang, {
      en: "Download verified engineering specifications, tolerance charts, and wire profile catalogs.",
      zh: "下载经检验的工程规范、公差图表及楔形丝型材目录。",
      es: "Descargue especificaciones de ingeniería verificadas, tablas de tolerancias y catálogos de perfiles.",
      de: "Herunterladen geprüfter technischer Spezifikationen, Toleranztabellen und Profildrahtkatalogen.",
      fr: "Téléchargez les spécifications validées, chartes de tolérances et catalogues de profils.",
      pt: "Baixe especificações de engenharia verificadas, tabelas de tolerâncias e catálogos de perfis."
    }),
    viewDownloads: pick(lang, {
      en: "View All Downloads",
      zh: "查看全部下载",
      es: "Ver todas las descargas",
      de: "Alle Downloads anzeigen",
      fr: "Voir tous les téléchargements",
      pt: "Ver todos os downloads"
    })
  };
  const specificationChecklist = [{
    parameter: pick(lang, {
      en: "Slot Opening (Aperture)",
      zh: "缝隙开孔 (Slot Opening)",
      es: "Abertura de ranura (Slot Opening)",
      de: "Spaltweite (Slot Opening)",
      fr: "Ouverture de fente (Slot Opening)",
      pt: "Abertura de fenda (Slot Opening)"
    }),
    industryStandard: pick(lang, {
      en: "0.02 mm to 5.0 mm (20 µm to 5,000 µm)",
      zh: "0.02 mm 至 5.0 mm",
      es: "0,02 mm a 5,0 mm (20 µm a 5.000 µm)",
      de: "0,02 mm bis 5,0 mm (20 µm bis 5.000 µm)",
      fr: "0,02 mm à 5,0 mm (20 µm à 5 000 µm)",
      pt: "0,02 mm a 5,0 mm (20 µm a 5.000 µm)"
    }),
    typicalTolerance: pick(lang, {
      en: "±0.015 mm for <0.10 mm, ±0.05 mm for ≥0.50 mm",
      zh: "<0.10 mm 时 ±0.015 mm，≥0.50 mm 时 ±0.05 mm",
      es: "±0,015 mm para <0,10 mm, ±0,05 mm para ≥0,50 mm",
      de: "±0,015 mm bei <0,10 mm, ±0,05 mm bei ≥0,50 mm",
      fr: "±0,015 mm pour <0,10 mm, ±0,05 mm pour ≥0,50 mm",
      pt: "±0,015 mm para <0,10 mm, ±0,05 mm para ≥0,50 mm"
    }),
    poRecommendation: pick(lang, {
      en: "Specify nominal mm/micron and maximum allowable variation across screen face",
      zh: "明确公称毫米/微米值以及沿筛网表面允许的最大公差变动范围",
      es: "Especifique milímetros/micrones nominales y la variación máxima admisible",
      de: "Nennspaltweite in mm/µm sowie maximal zulässige Abweichung auf der Siebfläche angeben",
      fr: "Spécifiez la valeur nominale en mm/microns et la variation maximale admissible sur la surface",
      pt: "Especifique valores nominais em mm/mícrons e a variação máxima permitida na tela"
    }),
    criticalNote: pick(lang, {
      en: "Specify optical verification protocol for fine slot sizes (<50 µm).",
      zh: "对微细缝隙（<50 µm）建议要求提供光学显微影像实测报告与校准记录。",
      es: "Recomiende protocolo de verificación óptica para ranuras finas (<50 µm).",
      de: "Für Feinspalte (<50 µm) optisches Messprotokoll mit Kalibrierung anfordern.",
      fr: "Pour les fentes fines (<50 µm), prévoyez un protocole de mesure optique étalonné.",
      pt: "Para fendas finas (<50 µm), solicite protocolo óptico de medição e calibração."
    })
  }, {
    parameter: pick(lang, {
      en: "Surface Profile Wire",
      zh: "表面楔形丝型材 (Profile Wire)",
      es: "Perfil de alambre de superficie",
      de: "Oberflächendrahtprofil (V-Draht)",
      fr: "Fil profilé de surface (Profil en V)",
      pt: "Perfil de arame de superfície"
    }),
    industryStandard: pick(lang, {
      en: "V-wire profiles (e.g., 12S, 18S, 22S, 28S, 34S)",
      zh: "V型丝型材（如 12S, 18S, 22S, 28S, 34S）",
      es: "Perfiles en V (p. ej., 12S, 18S, 22S, 28S, 34S)",
      de: "V-Drahtprofile (z. B. 12S, 18S, 22S, 28S, 34S)",
      fr: "Profils en V (ex. 12S, 18S, 22S, 28S, 34S)",
      pt: "Perfis em V (ex.: 12S, 18S, 22S, 28S, 34S)"
    }),
    typicalTolerance: pick(lang, {
      en: "Wire width: ±0.03 mm, Relief angle: 10° to 35°",
      zh: "丝宽：±0.03 mm，释放角：10° 至 35°",
      es: "Ancho de alambre: ±0,03 mm, Ángulo de alivio: 10° a 35°",
      de: "Drahtbreite: ±0,03 mm, Freiwinkel: 10° bis 35°",
      fr: "Largeur du fil : ±0,03 mm, Angle de dépouille : 10° à 35°",
      pt: "Largura do arame: ±0,03 mm, Ângulo de alívio: 10° a 35°"
    }),
    poRecommendation: pick(lang, {
      en: "State wire profile code, surface width, and relief angle for anti-clogging",
      zh: "注明型材截面代号、表面丝宽及防堵塞背部释放角",
      es: "Indique código de perfil, ancho superficial y ángulo de alivio anti-obstrucción",
      de: "Profilcode, Oberflächenbreite und Freiwinkel gegen Verstopfung angeben",
      fr: "Indiquez le code du profil, la largeur de surface et l'angle anti-colmatage",
      pt: "Indique código do perfil, largura superficial e ângulo de alívio anti-entupimento"
    }),
    criticalNote: pick(lang, {
      en: "Sharp top edges required for dewatering; rounded edges for delicate media.",
      zh: "固液分离脱水需锐利上边缘；敏感介质或滤膜载体需倒圆光滑边缘。",
      es: "Bordes superiores afilados para deshidratación; bordes redondeados para medios delicados.",
      de: "Scharfe Oberkanten für Entwässerung; gerundete Kanten für empfindliche Filtrationsgüter.",
      fr: "Arêtes vives pour l'égouttage; bords adoucis pour les médias sensibles.",
      pt: "Arestas superiores vivas para desaguamento; bordas arredondadas para mídias sensíveis."
    })
  }, {
    parameter: pick(lang, {
      en: "Support Rod Pitch & Profile",
      zh: "支撑筋条截面与间距 (Support Pitch)",
      es: "Paso y perfil de varillas de soporte",
      de: "Tragstabprofil & Teilungsabstand",
      fr: "Pas et profil des tiges de support",
      pt: "Passo e perfil das hastes de suporte"
    }),
    industryStandard: pick(lang, {
      en: "Round bars (Q25/Q35), teardrop rods, or flat bars at 10 mm to 50 mm pitch",
      zh: "圆棒 (Q25/Q35)、水滴型杆或扁钢，间距 10 mm 至 50 mm",
      es: "Barras redondas (Q25/Q35), en gota o pletinas con paso de 10 mm a 50 mm",
      de: "Rundstäbe (Q25/Q35), Tropfenprofile oder Flachstahl bei 10 mm bis 50 mm Teilung",
      fr: "Barres rondes (Q25/Q35), profil goutte d'eau ou méplats au pas de 10 mm à 50 mm",
      pt: "Barras redondas (Q25/Q35), gota ou barras chatas com passo de 10 mm a 50 mm"
    }),
    typicalTolerance: pick(lang, {
      en: "Pitch spacing: ±1.0 mm, Rod angle: 90° ± 0.5°",
      zh: "间距公差：±1.0 mm，垂直交角：90° ± 0.5°",
      es: "Espaciado de paso: ±1,0 mm, Ángulo de varilla: 90° ± 0,5°",
      de: "Teilungsabstand: ±1,0 mm, Kreuzungswinkel: 90° ± 0,5°",
      fr: "Espacement du pas : ±1,0 mm, Angle des tiges : 90° ± 0,5°",
      pt: "Espaçamento do passo: ±1,0 mm, Ângulo da haste: 90° ± 0,5°"
    }),
    poRecommendation: pick(lang, {
      en: "State internal collapse pressure, differential load, and center-to-center pitch",
      zh: "注明内部压溃载荷、差压要求及支撑筋条中心距",
      es: "Indique presión de colapso, carga diferencial y paso entre centros",
      de: "Kollapsdruck, Differenzdrucklast und Achsabstand angeben",
      fr: "Indiquez la pression d'écrasement, la charge différentielle et l'entraxe",
      pt: "Indique pressão de colapso, carga diferencial e passo de centro a centro"
    }),
    criticalNote: pick(lang, {
      en: "High-pressure backwash cycles require reinforced support rods.",
      zh: "高压反冲洗或周期性脉冲工况建议采用加厚加固型支撑筋条。",
      es: "Ciclos de retrolavado a alta presión requieren varillas reforzadas.",
      de: "Hochdruck-Rückspülzyklen erfordern verstärkte Tragstabquerschnitte.",
      fr: "Les cycles de rétrolavage haute pression exigent des tiges de support renforcées.",
      pt: "Ciclos de retrolavagem de alta pressão exigem hastes reforçadas."
    })
  }, {
    parameter: pick(lang, {
      en: "Material Alloy & Heat Treatment",
      zh: "材料合金与热处理 (Alloy Grade)",
      es: "Aleación de material y tratamiento térmico",
      de: "Werkstofflegierung & Wärmebehandlung",
      fr: "Nuance d'alliage et traitement thermique",
      pt: "Liga metálica e tratamento térmico"
    }),
    industryStandard: pick(lang, {
      en: "AISI 304, 316L, 321, 2205 Duplex, Hastelloy C-276",
      zh: "AISI 304, 316L, 321, 2205 双相钢, 哈氏合金 C-276",
      es: "AISI 304, 316L, 321, 2205 Dúplex, Hastelloy C-276",
      de: "AISI 304, 316L, 321, 2205 Duplex, Hastelloy C-276",
      fr: "AISI 304, 316L, 321, 2205 Duplex, Hastelloy C-276",
      pt: "AISI 304, 316L, 321, 2205 Duplex, Hastelloy C-276"
    }),
    typicalTolerance: pick(lang, {
      en: "ASTM A276 / ASTM A580 chemical & mechanical bounds",
      zh: "ASTM A276 / ASTM A580 化学与机械性能界限",
      es: "Límites químicos y mecánicos según ASTM A276 / ASTM A580",
      de: "Chemische & mechanische Grenzwerte nach ASTM A276 / ASTM A580",
      fr: "Limites chimiques et mécaniques selon ASTM A276 / ASTM A580",
      pt: "Limites químicos e mecânicos conforme ASTM A276 / ASTM A580"
    }),
    poRecommendation: pick(lang, {
      en: "Material test certificate (MTR) and PMI report available upon request",
      zh: "可按需提供材质证明书 (MTR) 与 PMI 光谱检测报告",
      es: "Certificado de materiales (MTR) e informe PMI disponibles a pedido",
      de: "Werksprüfzeugnis (MTR) und PMI-Prüfbericht auf Anfrage verfügbar",
      fr: "Certificat matière (MTR) et rapport PMI disponibles sur demande",
      pt: "Certificado de ensaio (MTR) e laudo PMI disponíveis sob pedido"
    }),
    criticalNote: pick(lang, {
      en: "Specify low-carbon 316L to prevent intergranular sensitization during welding.",
      zh: "焊接构件建议指定 316L 低碳牌号以防止焊接热影响区晶间腐蚀。",
      es: "Especifique 316L bajo en carbono para evitar sensibilización intergranular en soldadura.",
      de: "Kohlenstoffarmen 316L wählen, um interkristalline Korrosion beim Schweißen zu verhindern.",
      fr: "Spécifiez le 316L bas carbone pour prévenir la corrosion intergranulaire en soudure.",
      pt: "Especifique 316L de baixo carbono para evitar sensitização intergranular na solda."
    })
  }, {
    parameter: pick(lang, {
      en: "Dimensional & Flatness Tolerances",
      zh: "外形尺寸与几何公差 (Tolerances)",
      es: "Tolerancias dimensionales y de planitud",
      de: "Maß- & Ebenheitstoleranzen",
      fr: "Tolérances dimensionnelles et de planéité",
      pt: "Tolerâncias dimensionais e de planicidade"
    }),
    industryStandard: pick(lang, {
      en: "Panels, cylinders, sieve bends, or baskets",
      zh: "平板筛板、圆柱滤筒、弧形筛板或滤篮",
      es: "Paneles, cilindros, tamices curvados o cestas",
      de: "Flachpaneele, Zylinder, Bogensiebe oder Körbe",
      fr: "Panneaux, cylindres, grilles courbes ou paniers",
      pt: "Painéis, cilindros, telas curvas ou cestos"
    }),
    typicalTolerance: pick(lang, {
      en: "Length/Width: ±1.0 mm, Diagonal squareness: < 1.5 mm, Flatness: < 2 mm/m",
      zh: "长/宽：±1.0 mm，对角线：< 1.5 mm，平面度：< 2 mm/m",
      es: "Largo/Ancho: ±1,0 mm, Escuadra diagonal: < 1,5 mm, Planitud: < 2 mm/m",
      de: "Länge/Breite: ±1,0 mm, Rechtwinkligkeit Diagonale: < 1,5 mm, Ebenheit: < 2 mm/m",
      fr: "Longueur/Largeur : ±1,0 mm, Équerrage diagonal : < 1,5 mm, Planéité : < 2 mm/m",
      pt: "Comprimento/Largura: ±1,0 mm, Esquadro diagonal: < 1,5 mm, Planicidade: < 2 mm/m"
    }),
    poRecommendation: pick(lang, {
      en: "Define overall length, outer diameter (OD/ID), ovality, and edge margin",
      zh: "明确总长、外径/内径 (OD/ID)、椭圆度及边框余量",
      es: "Defina longitud total, diámetro exterior/interior (OD/ID), ovalización y margen de borde",
      de: "Gesamtlänge, Außen-/Innendurchmesser (AD/ID), Rundheit und Randabstände angeben",
      fr: "Définissez la longueur totale, diamètre externe/interne (OD/ID), ovalisation et marges",
      pt: "Defina comprimento total, diâmetro externo/interno (OD/ID), ovalização e margens"
    }),
    criticalNote: pick(lang, {
      en: "State whether slot direction runs axial (FOTI/FITO) or circumferential.",
      zh: "指明缝隙走向为轴向连续（FOTI/FITO）还是沿外圆周环向排列。",
      es: "Indique si la ranura es axial (FOTI/FITO) o circunferencial.",
      de: "Festlegen, ob die Spaltrichtung axial (FOTI/FITO) oder radial umlaufend ist.",
      fr: "Indiquez si la fente est orientée axialement (FOTI/FITO) ou circonférentiellement.",
      pt: "Indique se as fendas são axiais (FOTI/FITO) ou circunferenciais."
    })
  }, {
    parameter: pick(lang, {
      en: "Edge Treatment & End Fittings",
      zh: "边缘工艺与端部配件 (Fittings)",
      es: "Tratamiento de bordes y conexiones terminales",
      de: "Randbearbeitung & Endanschlüsse",
      fr: "Traitement des bords et raccords d'extrémité",
      pt: "Tratamento de bordas e conexões terminais"
    }),
    industryStandard: pick(lang, {
      en: "Raw sheared edge, solid framing flats, welded flanges, NPT/BSP threads",
      zh: "原始剪切边、实心包边扁钢、焊接法兰、NPT/BSP 螺纹接头",
      es: "Borde cortado en bruto, pletinas de marco macizo, bridas soldadas, roscas NPT/BSP",
      de: "Scherenschnittkante, massive Flachstahlrahmen, Schweißflansche, NPT/BSP-Gewinde",
      fr: "Bords bruts cisaillés, encadrement méplat massif, brides soudées, filetages NPT/BSP",
      pt: "Bordas brutas cisalhadas, moldura em barra chata, flanges soldadas, roscas NPT/BSP"
    }),
    typicalTolerance: pick(lang, {
      en: "Weld seam thickness: ±0.5 mm, Flange hole PCD: ±0.2 mm",
      zh: "焊缝余高：±0.5 mm，法兰螺栓孔 PCD：±0.2 mm",
      es: "Espesor de cordón de soldadura: ±0,5 mm, PCD de taladros: ±0,2 mm",
      de: "Schweißnahtdicke: ±0,5 mm, Flansch-Lochkreis: ±0,2 mm",
      fr: "Surépaisseur de soudure : ±0,5 mm, PCD trous de bride : ±0,2 mm",
      pt: "Espessura do cordão de solda: ±0,5 mm, PCD dos furos: ±0,2 mm"
    }),
    poRecommendation: pick(lang, {
      en: "Provide dimensional drawing with flange bolt patterns and end weld finishing",
      zh: "提供包含法兰螺栓孔距与端部焊缝打磨要求的工程图纸",
      es: "Proporcione plano acotado con patrón de tornillos y acabado de soldadura",
      de: "Maßzeichnung mit Flansch-Lochbild und Nahtnachbearbeitung beilegen",
      fr: "Fournir un plan dimensionnel avec entraxe des boulons et finition de soudure",
      pt: "Fornecer desenho com padrão de furação de flange e acabamento de solda"
    }),
    criticalNote: pick(lang, {
      en: "Specify pickled & passivated (ASTM A380) or mechanically polished surface condition.",
      zh: "注明酸洗钝化 (ASTM A380) 或机械抛光表面处理要求。",
      es: "Especifique decapado y pasivado (ASTM A380) o pulido mecánico.",
      de: "Beizen & Passivieren (ASTM A380) oder mechanisches Polieren angeben.",
      fr: "Spécifiez l'état de surface décapé-passivé (ASTM A380) ou poli mécaniquement.",
      pt: "Especifique decapagem e passivação (ASTM A380) ou polimento mecânico."
    })
  }];
  const currentSnippet = pick(lang, samplePoSnippet);
  const handleCopy = () => {
    navigator.clipboard.writeText(currentSnippet);
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  };
  return <section id="wedgewirepurchasespecifications" data-nav="light" className="border-b border-border bg-background mf-section text-foreground">
      <div className="mf-container">
        {/* Header Block */}
        <div className="max-w-3xl space-y-4">
          <Badge variant="outline" className="gap-1.5 rounded-md border-border bg-card px-3 py-1 font-mono mf-caption tracking-tight text-foreground">
            <Sliders className="size-3.5 text-primary" aria-hidden="true" />
            <span>{copy.eyebrow}</span>
          </Badge>
          <h2 className="mf-h2 text-foreground">
            {copy.heading}
          </h2>
          <p className="mf-lead text-muted-foreground">
            {copy.lead}
          </p>
        </div>

        {/* Core Parameters Table */}
        <div className="mt-12 overflow-hidden rounded-lg border border-border bg-card">
          <div className="flex flex-col justify-between gap-4 border-b border-border p-6 sm:flex-row sm:items-center">
            <div>
              <h3 className="mf-h3 text-card-foreground">
                {copy.tableTitle}
              </h3>
              <p className="mf-caption text-muted-foreground sm:text-sm">
                {copy.tableSubtitle}
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <Button asChild variant="outline" size="sm">
                <Link to="/RequestAQuotePage" className="inline-flex items-center gap-1.5">
                  <span>{copy.rfqBtn}</span>
                  <ArrowRight className="size-3.5" aria-hidden="true" />
                </Link>
              </Button>
            </div>
          </div>

          <TooltipProvider>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/40">
                    <TableHead className="w-[200px] mf-caption font-semibold uppercase tracking-wider text-muted-foreground">
                      {copy.thParameter}
                    </TableHead>
                    <TableHead className="w-[240px] mf-caption font-semibold uppercase tracking-wider text-muted-foreground">
                      {copy.thRange}
                    </TableHead>
                    <TableHead className="w-[220px] mf-caption font-semibold uppercase tracking-wider text-muted-foreground">
                      {copy.thTolerance}
                    </TableHead>
                    <TableHead className="mf-caption font-semibold uppercase tracking-wider text-muted-foreground">
                      {copy.thClause}
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {specificationChecklist.map((item, i) => <TableRow key={i} data-index={i} className="hover:bg-muted/20">
                      <TableCell className="font-medium text-foreground">
                        <div className="flex items-center gap-2">
                          <span>{item.parameter}</span>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className="cursor-help text-muted-foreground hover:text-foreground">
                                <Info className="size-3.5" aria-hidden="true" />
                              </span>
                            </TooltipTrigger>
                            <TooltipContent className="max-w-xs mf-caption">
                              {item.criticalNote}
                            </TooltipContent>
                          </Tooltip>
                        </div>
                      </TableCell>
                      <TableCell className="font-mono mf-caption text-muted-foreground">
                        {item.industryStandard}
                      </TableCell>
                      <TableCell className="mf-caption font-medium text-foreground">
                        <span className="rounded border border-border bg-muted px-2 py-1 font-mono mf-caption text-foreground">
                          {item.typicalTolerance}
                        </span>
                      </TableCell>
                      <TableCell className="mf-caption text-muted-foreground">
                        {item.poRecommendation}
                      </TableCell>
                    </TableRow>)}
                </TableBody>
              </Table>
            </div>
          </TooltipProvider>
        </div>

        {/* 3-Column Engineering Detail Cards */}
        <div className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-3">
          <Card className="rounded-lg border-border bg-card">
            <CardHeader className="pb-3">
              <span className="inline-flex size-10 items-center justify-center rounded-md border border-border bg-muted text-primary">
                <Ruler className="size-5" aria-hidden="true" />
              </span>
              <CardTitle className="mt-4 text-base font-semibold text-card-foreground">
                {copy.card1Title}
              </CardTitle>
              <CardDescription className="mf-caption">
                {copy.card1Desc}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3 pt-0 mf-caption text-muted-foreground">
              <div className="flex justify-between border-b border-border/60 pb-1.5">
                <span>{copy.card1L1Label}</span>
                <span className="font-mono font-medium text-foreground">0.02 – 5.0 mm</span>
              </div>
              <div className="flex justify-between border-b border-border/60 pb-1.5">
                <span>{copy.card1L2Label}</span>
                <span className="font-mono font-medium text-foreground">10°, 12°, 18°, 22°</span>
              </div>
              <div className="flex justify-between border-b border-border/60 pb-1.5">
                <span>{copy.card1L3Label}</span>
                <span className="font-mono font-medium text-foreground">
                  {copy.card1L3Val}
                </span>
              </div>
              <div className="pt-2">
                <p className="leading-relaxed">
                  {copy.card1Text}
                </p>
              </div>
            </CardContent>
          </Card>

          <Card className="rounded-lg border-border bg-card">
            <CardHeader className="pb-3">
              <span className="inline-flex size-10 items-center justify-center rounded-md border border-border bg-muted text-primary">
                <ShieldCheck className="size-5" aria-hidden="true" />
              </span>
              <CardTitle className="mt-4 text-base font-semibold text-card-foreground">
                {copy.card2Title}
              </CardTitle>
              <CardDescription className="mf-caption">
                {copy.card2Desc}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3 pt-0 mf-caption text-muted-foreground">
              <div className="flex justify-between border-b border-border/60 pb-1.5">
                <span>{copy.card2L1Label}</span>
                <span className="font-mono font-medium text-foreground">AISI 304, 316L, 321</span>
              </div>
              <div className="flex justify-between border-b border-border/60 pb-1.5">
                <span>{copy.card2L2Label}</span>
                <span className="font-mono font-medium text-foreground">2205, Hastelloy C-276</span>
              </div>
              <div className="flex justify-between border-b border-border/60 pb-1.5">
                <span>{copy.card2L3Label}</span>
                <span className="font-mono font-medium text-foreground">{copy.card2L3Val}</span>
              </div>
              <div className="pt-2">
                <p className="leading-relaxed">
                  {copy.card2Text}
                </p>
              </div>
            </CardContent>
          </Card>

          <Card className="rounded-lg border-border bg-card">
            <CardHeader className="pb-3">
              <span className="inline-flex size-10 items-center justify-center rounded-md border border-border bg-muted text-primary">
                <Boxes className="size-5" aria-hidden="true" />
              </span>
              <CardTitle className="mt-4 text-base font-semibold text-card-foreground">
                {copy.card3Title}
              </CardTitle>
              <CardDescription className="mf-caption">
                {copy.card3Desc}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3 pt-0 mf-caption text-muted-foreground">
              <div className="flex justify-between border-b border-border/60 pb-1.5">
                <span>{copy.card3L1Label}</span>
                <span className="font-mono font-medium text-foreground">
                  {copy.card3L1Val}
                </span>
              </div>
              <div className="flex justify-between border-b border-border/60 pb-1.5">
                <span>{copy.card3L2Label}</span>
                <span className="font-mono font-medium text-foreground">
                  {copy.card3L2Val}
                </span>
              </div>
              <div className="flex justify-between border-b border-border/60 pb-1.5">
                <span>{copy.card3L3Label}</span>
                <span className="font-mono font-medium text-foreground">
                  {copy.card3L3Val}
                </span>
              </div>
              <div className="pt-2">
                <p className="leading-relaxed">
                  {copy.card3Text}
                </p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Copy-Ready PO Spec Block & Guide */}
        <div className="mt-12 grid grid-cols-1 gap-8 lg:grid-cols-12">
          <div className="space-y-4 lg:col-span-7">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <FileSpreadsheet className="size-4 text-primary" aria-hidden="true" />
                <h3 className="text-base font-semibold text-foreground">
                  {copy.templateTitle}
                </h3>
              </div>
              <Button variant="outline" size="sm" onClick={handleCopy} className="gap-1.5 mf-caption">
                {copied ? <>
                    <Check className="size-3.5 text-primary" aria-hidden="true" />
                    <span>{copy.copiedBtn}</span>
                  </> : <>
                    <Copy className="size-3.5" aria-hidden="true" />
                    <span>{copy.copyBtn}</span>
                  </>}
              </Button>
            </div>
            <div className="relative rounded-lg border border-border bg-muted/40 p-4 font-mono mf-caption leading-relaxed text-foreground/90 overflow-x-auto">
              <pre className="whitespace-pre">{currentSnippet}</pre>
            </div>
            <p className="mf-caption text-muted-foreground">
              {copy.templateNote}
            </p>
          </div>

          <div className="flex flex-col justify-between rounded-lg border border-border bg-card p-6 lg:col-span-5">
            <div className="space-y-4">
              <div className="inline-flex items-center gap-1.5 mf-caption font-semibold uppercase tracking-wider text-primary">
                <Cpu className="size-4" aria-hidden="true" />
                <span>{copy.reviewEyebrow}</span>
              </div>
              <h3 className="mf-h3 text-card-foreground">
                {copy.reviewTitle}
              </h3>
              <p className="mf-caption leading-relaxed text-muted-foreground sm:text-sm">
                {copy.reviewDesc}
              </p>
              <ul className="space-y-2 mf-caption text-foreground/90">
                <li className="flex items-center gap-2">
                  <CircleCheck className="size-4 text-primary shrink-0" aria-hidden="true" />
                  <span>{copy.reviewPt1}</span>
                </li>
                <li className="flex items-center gap-2">
                  <CircleCheck className="size-4 text-primary shrink-0" aria-hidden="true" />
                  <span>{copy.reviewPt2}</span>
                </li>
                <li className="flex items-center gap-2">
                  <CircleCheck className="size-4 text-primary shrink-0" aria-hidden="true" />
                  <span>{copy.reviewPt3}</span>
                </li>
              </ul>
            </div>

            <div className="mt-6 pt-4 border-t border-border flex flex-col gap-2 sm:flex-row">
              <Button asChild className="w-full sm:w-auto">
                <Link to="/ContactPage">
                  {copy.speakEngineer}
                </Link>
              </Button>
              <Button asChild variant="outline" className="w-full sm:w-auto">
                <Link to="/WedgeWireScreenSelectionGuidePage">
                  {copy.selectionGuideBtn}
                </Link>
              </Button>
            </div>
          </div>
        </div>

        {/* Technical Documents Archive Provider Integration */}
        <div className="mt-16 space-y-6">
          <div className="flex flex-col justify-between gap-2 border-b border-border pb-4 sm:flex-row sm:items-end">
            <div>
              <h3 className="mf-h3 text-foreground">
                {copy.docsTitle}
              </h3>
              <p className="mf-caption text-muted-foreground sm:text-sm">
                {copy.docsDesc}
              </p>
            </div>
            <Button asChild variant="ghost" size="sm" className="mf-caption text-primary">
              <Link to="/DownloadsPage" className="inline-flex items-center gap-1">
                <span>{copy.viewDownloads}</span>
                <ArrowRight className="size-3.5" aria-hidden="true" />
              </Link>
            </Button>
          </div>

          <PostsProvider className="technical-docs-provider" wp_query={{
          post_type: "technical_document",
          per_page: 3,
          status: ["publish"]
        }}>
            <TechnicalDocsConsumer lang={lang} />
          </PostsProvider>
        </div>
      </div>
    </section>;
}